import React, { useEffect, useState } from "react";
import DashboardShell from "@/components/DashboardShell";
import { useProject } from "@/context/ProjectContext";
import api from "@/lib/api";
import { Bell, Plus, Trash2 } from "lucide-react";

const METRICS = [
  { value: "bvs_drop", label: "BVS drops" },
  { value: "mention_rate_drop", label: "Mention rate drops" },
  { value: "competitor_surge", label: "Competitor surge" },
];

export default function Alerts() {
  const { current, projects, selectProject } = useProject();
  const [alerts, setAlerts] = useState([]);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [metric, setMetric] = useState("bvs_drop");
  const [threshold, setThreshold] = useState(10);
  const [loading, setLoading] = useState(false);

  const refresh = async () => {
    if (!current) return;
    const r = await api.get(`/projects/${current.project_id}/alerts`);
    setAlerts(r.data);
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [current?.project_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!current) return;
    setLoading(true);
    try {
      await api.post(`/projects/${current.project_id}/alerts`, {
        name,
        webhook_url: url,
        metric,
        threshold: Number(threshold),
        enabled: true,
      });
      setName("");
      setUrl("");
      await refresh();
    } finally {
      setLoading(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/alerts/${id}`);
    refresh();
  };

  return (
    <DashboardShell project={current} projects={projects} onProjectChange={selectProject}>
      <div className="mb-8">
        <div className="label-eyebrow">Realtime</div>
        <h1 className="display mt-2 text-4xl font-black tracking-tighter">
          Alerts &amp; Webhooks
        </h1>
        <p className="mt-2 text-zinc-600 text-sm max-w-xl">
          Plug a webhook into Slack, Linear, or a pager. We'll POST the full
          metric payload the moment a threshold fires.
        </p>
      </div>

      <form onSubmit={submit} className="card-flat p-5 mb-6 grid grid-cols-12 gap-3" data-testid="alert-form">
        <div className="col-span-12 lg:col-span-3">
          <label className="label-eyebrow">Name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Slack #marketing"
            required
            data-testid="alert-name"
            className="mt-1 w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
          />
        </div>
        <div className="col-span-12 lg:col-span-4">
          <label className="label-eyebrow">Webhook URL</label>
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://hooks.slack.com/…"
            required
            data-testid="alert-url"
            className="mt-1 w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
          />
        </div>
        <div className="col-span-8 lg:col-span-3">
          <label className="label-eyebrow">Metric</label>
          <select
            value={metric}
            onChange={(e) => setMetric(e.target.value)}
            data-testid="alert-metric"
            className="mt-1 w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
          >
            {METRICS.map((m) => (
              <option key={m.value} value={m.value}>
                {m.label}
              </option>
            ))}
          </select>
        </div>
        <div className="col-span-4 lg:col-span-1">
          <label className="label-eyebrow">≥</label>
          <input
            type="number"
            step="0.1"
            value={threshold}
            onChange={(e) => setThreshold(e.target.value)}
            data-testid="alert-threshold"
            className="mt-1 w-full bg-white border border-zinc-200 rounded-[4px] px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
          />
        </div>
        <div className="col-span-12 lg:col-span-1 flex items-end">
          <button
            type="submit"
            disabled={loading}
            data-testid="alert-add"
            className="w-full inline-flex justify-center items-center gap-1.5 bg-black text-white text-sm px-3 py-2 rounded-[4px] hover:bg-zinc-800 transition-colors disabled:opacity-50"
          >
            <Plus size={14} />
          </button>
        </div>
      </form>

      <section className="card-flat" data-testid="alerts-list">
        <div className="divide-y divide-zinc-200">
          {alerts.map((a) => (
            <div key={a.alert_id} className="p-4 grid grid-cols-12 gap-4 items-center text-sm">
              <div className="col-span-3 flex items-center gap-2 font-medium">
                <Bell size={14} /> {a.name}
              </div>
              <div className="col-span-4 mono text-xs text-zinc-500 truncate">
                {a.webhook_url}
              </div>
              <div className="col-span-2">{a.metric.replaceAll("_", " ")}</div>
              <div className="col-span-2 mono">≥ {a.threshold}</div>
              <div className="col-span-1 text-right">
                <button
                  onClick={() => remove(a.alert_id)}
                  data-testid={`alert-delete-${a.alert_id}`}
                  className="text-zinc-400 hover:text-rose-600 transition-colors"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
          ))}
          {!alerts.length && (
            <div className="p-8 text-sm text-zinc-500 text-center">
              No alerts yet.
            </div>
          )}
        </div>
      </section>
    </DashboardShell>
  );
}
